// "Things to do" entries: local relevance for area pages and collection pages
// (surf breaks, restaurants, yoga, waterfalls, supermarkets). Stored as a
// jsonb array on `areas.things_to_do` and `collections.things_to_do`; these
// helpers keep the shape honest on the way in and out.

export const THING_KINDS = [
  { id: "surf", label: "Surf" },
  { id: "food", label: "Food & drink" },
  { id: "yoga", label: "Yoga & wellness" },
  { id: "nature", label: "Nature" },
  { id: "shop", label: "Shops & services" },
  { id: "other", label: "Other" },
] as const;
export type ThingKind = (typeof THING_KINDS)[number]["id"];

export interface ThingToDo {
  name: string;
  kind: ThingKind;
  /** Minutes from the area or the typical home, on foot or by car as the entry says. */
  minutesAway?: number;
  url?: string;
  note?: string;
}

const isKind = (k: unknown): k is ThingKind => typeof k === "string" && THING_KINDS.some((t) => t.id === k);

/** Anything jsonb-shaped → a clean list; malformed entries are dropped, never thrown. */
export const parseThingsToDo = (value: unknown): ThingToDo[] => {
  if (!Array.isArray(value)) return [];
  const out: ThingToDo[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Record<string, unknown>;
    const name = typeof r.name === "string" ? r.name.trim() : "";
    if (!name) continue;
    const minutes = typeof r.minutes_away === "number" ? r.minutes_away : typeof r.minutesAway === "number" ? r.minutesAway : undefined;
    const url = typeof r.url === "string" && /^https?:\/\//i.test(r.url.trim()) ? r.url.trim() : undefined;
    const note = typeof r.note === "string" && r.note.trim() ? r.note.trim() : undefined;
    out.push({
      name,
      kind: isKind(r.kind) ? r.kind : "other",
      ...(minutes != null && minutes >= 0 ? { minutesAway: Math.round(minutes) } : {}),
      ...(url ? { url } : {}),
      ...(note ? { note } : {}),
    });
  }
  return out;
};

/** The jsonb shape written back (snake_case, no undefined keys). */
export const thingsToDoToJson = (items: readonly ThingToDo[]) =>
  items
    .filter((t) => t.name.trim())
    .map((t) => ({
      name: t.name.trim(),
      kind: t.kind,
      ...(t.minutesAway != null ? { minutes_away: t.minutesAway } : {}),
      ...(t.url ? { url: t.url } : {}),
      ...(t.note ? { note: t.note } : {}),
    }));

export const thingKindLabel = (kind: ThingKind): string => THING_KINDS.find((t) => t.id === kind)?.label ?? "Other";
